/**
 * WebOs Shell - Commandes Built-in
 *
 * Toutes les commandes Linux de base intégrées directement dans le shell.
 */

import { createLogger } from '../utils/debug.js';
import { getAuth } from '../kernel/auth.js';

const log = createLogger('Commands');

/**
 * Classe de gestion des commandes built-in
 */
export class Commands {
  constructor(shell) {
    this.shell = shell;
    this.fs = null;
    this.manPages = null;

    // Table des commandes
    this.commands = new Map([
      ['ls', this.ls.bind(this)],
      ['cd', this.cd.bind(this)],
      ['pwd', this.pwd.bind(this)],
      ['cat', this.cat.bind(this)],
      ['mkdir', this.mkdir.bind(this)],
      ['rm', this.rm.bind(this)],
      ['touch', this.touch.bind(this)],
      ['cp', this.cp.bind(this)],
      ['mv', this.mv.bind(this)],
      ['echo', this.echo.bind(this)],
      ['env', this.env.bind(this)],
      ['export', this.export.bind(this)],
      ['clear', this.clear.bind(this)],
      ['history', this.history.bind(this)],
      ['head', this.head.bind(this)],
      ['tail', this.tail.bind(this)],
      ['wc', this.wc.bind(this)],
      ['grep', this.grep.bind(this)],
      ['man', this.man.bind(this)],
      ['help', this.help.bind(this)],
      ['exit', this.exit.bind(this)],
      // Commandes utilisateur
      ['whoami', this.whoami.bind(this)],
      ['passwd', this.passwd.bind(this)],
      ['su', this.su.bind(this)],
      ['sudo', this.sudo.bind(this)],
      ['useradd', this.useradd.bind(this)],
      ['userdel', this.userdel.bind(this)],
      ['users', this.users.bind(this)],
      // Gestionnaire de paquets
      ['web', this.web.bind(this)],
      // Système
      ['hardreset', this.hardreset.bind(this)],
    ]);

    log.debug('Commands initialized');
  }

  /**
   * Configure les dépendances
   */
  configure(fs, manPages) {
    this.fs = fs;
    this.manPages = manPages;
  }

  /**
   * Vérifie si une commande existe
   */
  has(name) {
    return this.commands.has(name);
  }

  /**
   * Exécute une commande
   * @param {string} name - Nom de la commande
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe (optionnel)
   */
  async execute(name, args, stdin = null) {
    const cmd = this.commands.get(name);
    if (!cmd) {
      return { exitCode: 127, error: `${name}: commande introuvable` };
    }

    try {
      return await cmd(args, stdin);
    } catch (err) {
      log.error(`Command ${name} failed:`, err);
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * Résout un chemin (relatif -> absolu)
   */
  resolvePath(path) {
    if (!path) return this.shell.cwd;

    if (path.startsWith('~')) {
      path = this.shell.env.HOME + path.slice(1);
    } else if (!path.startsWith('/')) {
      path = this.shell.cwd === '/'
        ? '/' + path
        : this.shell.cwd + '/' + path;
    }

    // Normaliser (gérer . et ..)
    const parts = path.split('/').filter(p => p && p !== '.');
    const result = [];

    for (const part of parts) {
      if (part === '..') {
        result.pop();
      } else {
        result.push(part);
      }
    }

    return '/' + result.join('/');
  }

  /**
   * Parse les options d'une commande
   */
  parseArgs(args) {
    const options = {};
    const positional = [];

    for (const arg of args) {
      if (arg.startsWith('--')) {
        const [key, value] = arg.slice(2).split('=');
        options[key] = value ?? true;
      } else if (arg.startsWith('-') && arg.length > 1) {
        for (const char of arg.slice(1)) {
          options[char] = true;
        }
      } else {
        positional.push(arg);
      }
    }

    return { options, positional };
  }

  /**
   * Formate une taille en format lisible
   */
  formatSize(bytes) {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}K`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}M`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)}G`;
  }

  /**
   * Formate une date
   */
  formatDate(timestamp) {
    const date = new Date(timestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate().toString().padStart(2, ' ');
    const hours = date.getHours().toString().padStart(2, '0');
    const mins = date.getMinutes().toString().padStart(2, '0');
    return `${month} ${day} ${hours}:${mins}`;
  }

  // ==========================================
  // COMMANDES
  // ==========================================

  /**
   * ls - Liste le contenu d'un répertoire
   */
  async ls(args) {
    const { options, positional } = this.parseArgs(args);
    const path = this.resolvePath(positional[0] || '.');

    try {
      const entries = await this.fs.readdir(path);

      if (entries.length === 0) {
        return { exitCode: 0, output: '' };
      }

      // Filtrer les fichiers cachés si -a n'est pas spécifié
      let filtered = entries;
      if (!options.a) {
        filtered = entries.filter(e => !e.name.startsWith('.'));
      }

      // Tri alphabétique
      filtered.sort((a, b) => a.name.localeCompare(b.name));

      let output = '';

      if (options.l) {
        // Format long
        for (const entry of filtered) {
          const type = entry.type === 'directory' ? 'd' :
                       entry.type === 'symlink' ? 'l' : '-';
          const size = options.h
            ? this.formatSize(entry.size || 0).padStart(6)
            : (entry.size || 0).toString().padStart(8);
          const date = this.formatDate(entry.modified || Date.now());
          const name = entry.type === 'directory'
            ? `\x1b[1;34m${entry.name}\x1b[0m`
            : entry.type === 'symlink'
              ? `\x1b[1;36m${entry.name}\x1b[0m -> ${entry.target || '?'}`
              : entry.name;

          output += `${type}rwxr-xr-x  ${size} ${date} ${name}\n`;
        }
      } else {
        // Format simple (colonnes)
        const names = filtered.map(e => {
          if (e.type === 'directory') {
            return `\x1b[1;34m${e.name}\x1b[0m`;
          } else if (e.type === 'symlink') {
            return `\x1b[1;36m${e.name}\x1b[0m`;
          }
          return e.name;
        });

        output = names.join('  ') + '\n';
      }

      return { exitCode: 0, output };
    } catch (err) {
      return { exitCode: 1, error: `ls: ${path}: ${err.message}` };
    }
  }

  /**
   * cd - Change le répertoire courant
   */
  async cd(args) {
    const { positional } = this.parseArgs(args);
    let path = positional[0];

    // cd sans argument -> home
    if (!path) {
      path = this.shell.env.HOME || '/home/user';
    }

    // cd - -> répertoire précédent
    if (path === '-') {
      if (this.shell.previousCwd) {
        path = this.shell.previousCwd;
        this.shell.print(path + '\n');
      } else {
        return { exitCode: 1, error: 'cd: OLDPWD non défini' };
      }
    }

    const resolved = this.resolvePath(path);

    try {
      const stat = await this.fs.stat(resolved);

      if (stat.type !== 'directory') {
        return { exitCode: 1, error: `cd: ${path}: N'est pas un répertoire` };
      }

      this.shell.previousCwd = this.shell.cwd;
      this.shell.cwd = resolved;
      this.shell.env.PWD = resolved;

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: `cd: ${path}: ${err.message}` };
    }
  }

  /**
   * pwd - Affiche le répertoire courant
   */
  async pwd() {
    return { exitCode: 0, output: this.shell.cwd + '\n' };
  }

  /**
   * cat - Affiche le contenu d'un fichier ou de stdin
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe
   */
  async cat(args, stdin = null) {
    const { positional } = this.parseArgs(args);

    // Si stdin est fourni et pas de fichiers, afficher stdin
    if (stdin !== null && positional.length === 0) {
      let output = stdin;
      if (!output.endsWith('\n')) {
        output += '\n';
      }
      return { exitCode: 0, output };
    }

    if (positional.length === 0) {
      return { exitCode: 1, error: 'cat: argument manquant' };
    }

    let output = '';

    for (const file of positional) {
      const path = this.resolvePath(file);

      try {
        const content = await this.fs.readFile(path);
        output += content;

        // Ajouter un saut de ligne si nécessaire
        if (!content.endsWith('\n')) {
          output += '\n';
        }
      } catch (err) {
        return { exitCode: 1, error: `cat: ${file}: ${err.message}` };
      }
    }

    return { exitCode: 0, output };
  }

  /**
   * mkdir - Crée un répertoire
   */
  async mkdir(args) {
    const { options, positional } = this.parseArgs(args);

    if (positional.length === 0) {
      return { exitCode: 1, error: 'mkdir: argument manquant' };
    }

    for (const dir of positional) {
      const path = this.resolvePath(dir);

      try {
        await this.fs.mkdir(path, { recursive: options.p });
      } catch (err) {
        if (!options.p || err.code !== 'EEXIST') {
          return { exitCode: 1, error: `mkdir: ${dir}: ${err.message}` };
        }
      }
    }

    return { exitCode: 0 };
  }

  /**
   * rm - Supprime des fichiers ou répertoires
   */
  async rm(args) {
    const { options, positional } = this.parseArgs(args);

    if (positional.length === 0) {
      return { exitCode: 1, error: 'rm: argument manquant' };
    }

    for (const file of positional) {
      const path = this.resolvePath(file);

      try {
        const stat = await this.fs.stat(path);

        if (stat.type === 'directory' && !options.r) {
          return { exitCode: 1, error: `rm: ${file}: est un répertoire (utilisez -r)` };
        }

        await this.fs.delete(path, { recursive: options.r });
      } catch (err) {
        if (!options.f) {
          return { exitCode: 1, error: `rm: ${file}: ${err.message}` };
        }
      }
    }

    return { exitCode: 0 };
  }

  /**
   * touch - Crée un fichier vide ou met à jour le timestamp
   */
  async touch(args) {
    const { positional } = this.parseArgs(args);

    if (positional.length === 0) {
      return { exitCode: 1, error: 'touch: argument manquant' };
    }

    for (const file of positional) {
      const path = this.resolvePath(file);

      try {
        const exists = await this.fs.exists(path);

        if (exists) {
          // Mettre à jour le timestamp
          const content = await this.fs.readFile(path);
          await this.fs.writeFile(path, content);
        } else {
          // Créer un fichier vide
          await this.fs.writeFile(path, '');
        }
      } catch (err) {
        return { exitCode: 1, error: `touch: ${file}: ${err.message}` };
      }
    }

    return { exitCode: 0 };
  }

  /**
   * cp - Copie des fichiers
   */
  async cp(args) {
    const { options, positional } = this.parseArgs(args);

    if (positional.length < 2) {
      return { exitCode: 1, error: 'cp: arguments manquants (source destination)' };
    }

    const src = this.resolvePath(positional[0]);
    const dest = this.resolvePath(positional[1]);

    try {
      const srcStat = await this.fs.stat(src);

      if (srcStat.type === 'directory') {
        if (!options.r) {
          return { exitCode: 1, error: `cp: ${positional[0]}: est un répertoire (utilisez -r)` };
        }
        // TODO: Copie récursive
        return { exitCode: 1, error: 'cp -r: non implémenté pour l\'instant' };
      }

      const content = await this.fs.readFile(src, 'binary');
      await this.fs.writeFile(dest, content);

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: `cp: ${err.message}` };
    }
  }

  /**
   * mv - Déplace ou renomme des fichiers
   */
  async mv(args) {
    const { positional } = this.parseArgs(args);

    if (positional.length < 2) {
      return { exitCode: 1, error: 'mv: arguments manquants (source destination)' };
    }

    const src = this.resolvePath(positional[0]);
    const dest = this.resolvePath(positional[1]);

    try {
      const content = await this.fs.readFile(src, 'binary');
      await this.fs.writeFile(dest, content);
      await this.fs.delete(src);

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: `mv: ${err.message}` };
    }
  }

  /**
   * echo - Affiche du texte
   */
  async echo(args) {
    // Joindre les arguments et remplacer les variables
    let text = args.join(' ');

    // Remplacer les variables d'environnement
    text = text.replace(/\$(\w+)/g, (match, name) => {
      return this.shell.env[name] ?? '';
    });

    return { exitCode: 0, output: text + '\n' };
  }

  /**
   * env - Affiche les variables d'environnement
   */
  async env() {
    let output = '';

    for (const [key, value] of Object.entries(this.shell.env)) {
      output += `${key}=${value}\n`;
    }

    return { exitCode: 0, output };
  }

  /**
   * export - Définit une variable d'environnement
   */
  async export(args) {
    if (args.length === 0) {
      // Sans argument, affiche les exports
      return this.env();
    }

    for (const arg of args) {
      const eqIndex = arg.indexOf('=');
      if (eqIndex === -1) {
        return { exitCode: 1, error: `export: syntaxe invalide (utilisez NOM=VALEUR)` };
      }

      const name = arg.slice(0, eqIndex);
      const value = arg.slice(eqIndex + 1);

      this.shell.env[name] = value;
    }

    return { exitCode: 0 };
  }

  /**
   * clear - Efface l'écran
   */
  async clear() {
    this.shell.terminal.clear();
    return { exitCode: 0 };
  }

  /**
   * history - Affiche l'historique des commandes
   */
  async history() {
    let output = '';
    let i = 1;

    for (const cmd of this.shell.history) {
      output += `  ${i.toString().padStart(4)}  ${cmd}\n`;
      i++;
    }

    return { exitCode: 0, output };
  }

  /**
   * head - Affiche le début d'un fichier ou de stdin
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe
   */
  async head(args, stdin = null) {
    const { options, positional } = this.parseArgs(args);
    const lines = parseInt(options.n) || 10;

    let content;

    // Si stdin est fourni, l'utiliser
    if (stdin !== null) {
      content = stdin;
    } else if (positional.length === 0) {
      return { exitCode: 1, error: 'head: argument manquant' };
    } else {
      const path = this.resolvePath(positional[0]);
      try {
        content = await this.fs.readFile(path);
      } catch (err) {
        return { exitCode: 1, error: `head: ${positional[0]}: ${err.message}` };
      }
    }

    const allLines = content.split('\n');
    const output = allLines.slice(0, lines).join('\n') + '\n';

    return { exitCode: 0, output };
  }

  /**
   * tail - Affiche la fin d'un fichier ou de stdin
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe
   */
  async tail(args, stdin = null) {
    const { options, positional } = this.parseArgs(args);
    const lines = parseInt(options.n) || 10;

    let content;

    // Si stdin est fourni, l'utiliser
    if (stdin !== null) {
      content = stdin;
    } else if (positional.length === 0) {
      return { exitCode: 1, error: 'tail: argument manquant' };
    } else {
      const path = this.resolvePath(positional[0]);
      try {
        content = await this.fs.readFile(path);
      } catch (err) {
        return { exitCode: 1, error: `tail: ${positional[0]}: ${err.message}` };
      }
    }

    const allLines = content.split('\n');
    const output = allLines.slice(-lines).join('\n') + '\n';

    return { exitCode: 0, output };
  }

  /**
   * wc - Compte les lignes, mots et caractères
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe
   */
  async wc(args, stdin = null) {
    const { options, positional } = this.parseArgs(args);

    let content;
    let filename = '';

    // Si stdin est fourni, l'utiliser
    if (stdin !== null) {
      content = stdin;
    } else if (positional.length === 0) {
      return { exitCode: 1, error: 'wc: argument manquant' };
    } else {
      const path = this.resolvePath(positional[0]);
      filename = positional[0];
      try {
        content = await this.fs.readFile(path);
      } catch (err) {
        return { exitCode: 1, error: `wc: ${positional[0]}: ${err.message}` };
      }
    }

    const lines = content.split('\n').length - 1;
    const words = content.split(/\s+/).filter(w => w).length;
    const chars = content.length;

    let output = '';

    if (options.l && !options.w && !options.c) {
      output = `${lines}${filename ? ' ' + filename : ''}\n`;
    } else if (options.w && !options.l && !options.c) {
      output = `${words}${filename ? ' ' + filename : ''}\n`;
    } else if (options.c && !options.l && !options.w) {
      output = `${chars}${filename ? ' ' + filename : ''}\n`;
    } else {
      output = `  ${lines}   ${words} ${chars}${filename ? ' ' + filename : ''}\n`;
    }

    return { exitCode: 0, output };
  }

  /**
   * grep - Recherche un motif dans un fichier ou stdin
   * @param {string[]} args - Arguments
   * @param {string|null} stdin - Input depuis un pipe
   */
  async grep(args, stdin = null) {
    const { options, positional } = this.parseArgs(args);

    let pattern;
    let content;

    // Si stdin est fourni, le pattern est le premier argument
    if (stdin !== null) {
      if (positional.length < 1) {
        return { exitCode: 1, error: 'grep: motif manquant' };
      }
      pattern = positional[0];
      content = stdin;
    } else {
      // Mode traditionnel: pattern fichier
      if (positional.length < 2) {
        return { exitCode: 1, error: 'grep: arguments manquants (motif fichier)' };
      }
      pattern = positional[0];
      const path = this.resolvePath(positional[1]);
      try {
        content = await this.fs.readFile(path);
      } catch (err) {
        return { exitCode: 1, error: `grep: ${positional[1]}: ${err.message}` };
      }
    }

    const lines = content.split('\n');

    const regex = options.i
      ? new RegExp(pattern, 'i')
      : new RegExp(pattern);

    const matches = lines.filter(line => regex.test(line));

    if (matches.length === 0) {
      return { exitCode: 1, output: '' };
    }

    // Colorier les correspondances
    const output = matches.map(line => {
      return line.replace(regex, match => `\x1b[1;31m${match}\x1b[0m`);
    }).join('\n') + '\n';

    return { exitCode: 0, output };
  }

  /**
   * man - Affiche le manuel d'une commande
   */
  async man(args) {
    const { positional } = this.parseArgs(args);

    if (positional.length === 0) {
      return { exitCode: 1, error: 'man: quelle page de manuel voulez-vous?' };
    }

    const command = positional[0];
    const manual = await this.manPages.getManual(command);

    if (!manual) {
      return { exitCode: 1, error: `Pas d'entrée de manuel pour ${command}` };
    }

    return { exitCode: 0, output: '\n' + manual + '\n\n' };
  }

  /**
   * help - Affiche l'aide du shell
   */
  async help() {
    let output = `
\x1b[1;32mWebOs Shell - Commandes disponibles\x1b[0m

\x1b[1;34mNavigation:\x1b[0m
  ls [path]        Liste le contenu d'un répertoire
  cd [path]        Change le répertoire courant
  pwd              Affiche le répertoire courant

\x1b[1;34mFichiers:\x1b[0m
  cat <file>       Affiche le contenu d'un fichier
  head <file>      Affiche le début d'un fichier
  tail <file>      Affiche la fin d'un fichier
  touch <file>     Crée un fichier vide
  cp <src> <dst>   Copie un fichier
  mv <src> <dst>   Déplace/renomme un fichier
  rm <file>        Supprime un fichier

\x1b[1;34mRépertoires:\x1b[0m
  mkdir <dir>      Crée un répertoire
  rm -r <dir>      Supprime un répertoire

\x1b[1;34mRecherche:\x1b[0m
  grep <pat> <f>   Recherche un motif dans un fichier
  wc <file>        Compte lignes/mots/caractères

\x1b[1;34mEnvironnement:\x1b[0m
  env              Affiche les variables d'environnement
  export VAR=val   Définit une variable
  echo <text>      Affiche du texte

\x1b[1;34mShell:\x1b[0m
  clear            Efface l'écran
  history          Historique des commandes
  exit             Quitte le shell

\x1b[1;34mUtilisateurs:\x1b[0m
  whoami           Affiche l'utilisateur courant
  users            Liste les utilisateurs
  passwd           Change le mot de passe
  su <user>        Change d'utilisateur
  sudo <cmd>       Exécute en tant qu'admin
  useradd <user>   Crée un utilisateur (admin)
  userdel <user>   Supprime un utilisateur (admin)

\x1b[1;34mPaquets:\x1b[0m
  web install <p>  Installe un paquet
  web install --all Installe tous les paquets
  web uninstall <p> Désinstalle un paquet
  web list         Liste les paquets installés
  web search <q>   Recherche des paquets

\x1b[1;34mSystème:\x1b[0m
  hardreset        Réinitialise WebOs (factory reset)

\x1b[1;34mAide:\x1b[0m
  help             Cette aide
  man <cmd>        Manuel d'une commande
`;

    // Ajouter les commandes externes du registre
    const registry = this.manPages.getHelpRegistry();
    for (const category of this.manPages.getHelpCategories()) {
      const commands = registry.get(category);
      if (commands && commands.length > 0) {
        output += `\n\x1b[1;34m${category}:\x1b[0m\n`;
        for (const cmd of commands) {
          const name = cmd.name.padEnd(16);
          output += `  ${name} ${cmd.description}\n`;
        }
      }
    }

    output += `\nTapez \x1b[1mman <commande>\x1b[0m pour plus de détails.\n`;

    return { exitCode: 0, output };
  }

  // ==========================================
  // COMMANDES UTILISATEUR
  // ==========================================

  /**
   * whoami - Affiche l'utilisateur courant
   */
  async whoami() {
    const user = this.shell.env.USER;
    return { exitCode: 0, output: user + '\n' };
  }

  /**
   * passwd - Change le mot de passe
   */
  async passwd(args) {
    const auth = getAuth();
    const currentUser = this.shell.env.USER;
    const targetUser = args[0] || currentUser;

    // Si on change le mot de passe d'un autre utilisateur, il faut sudo
    if (targetUser !== currentUser) {
      const canSudo = await auth.canSudo();
      if (!canSudo) {
        return { exitCode: 1, error: 'Seul un administrateur peut changer le mot de passe d\'un autre utilisateur' };
      }
    }

    // Demander l'ancien mot de passe (sauf si admin change pour un autre)
    let oldPassword = '';
    if (targetUser === currentUser) {
      oldPassword = await this.promptPassword('Mot de passe actuel: ');
      if (!oldPassword) {
        return { exitCode: 1, error: 'Annulé' };
      }
    } else {
      // Admin doit confirmer son propre mot de passe
      oldPassword = await this.promptPassword('Votre mot de passe admin: ');
      const valid = await auth.verifySudo(oldPassword);
      if (!valid) {
        return { exitCode: 1, error: 'Mot de passe incorrect' };
      }
      // Pour l'admin qui change le mdp d'un autre, on a besoin de l'ancien mdp de la cible
      // Solution: on le force à un nouveau directement (comportement de passwd avec sudo)
    }

    // Demander le nouveau mot de passe
    const newPassword = await this.promptPassword('Nouveau mot de passe: ');
    if (!newPassword || newPassword.length < 4) {
      return { exitCode: 1, error: 'Le mot de passe doit faire au moins 4 caractères' };
    }

    const confirmPassword = await this.promptPassword('Confirmer le nouveau mot de passe: ');
    if (newPassword !== confirmPassword) {
      return { exitCode: 1, error: 'Les mots de passe ne correspondent pas' };
    }

    // Changer le mot de passe
    const result = await auth.changePassword(targetUser, oldPassword, newPassword);

    if (result.success) {
      return { exitCode: 0, output: 'Mot de passe modifié avec succès.\n' };
    } else {
      return { exitCode: 1, error: result.error };
    }
  }

  /**
   * su - Change d'utilisateur
   */
  async su(args) {
    const auth = getAuth();
    const targetUser = args[0];

    if (!targetUser) {
      return { exitCode: 1, error: 'Usage: su <utilisateur>' };
    }

    // Demander le mot de passe de l'utilisateur cible
    const password = await this.promptPassword(`Mot de passe de ${targetUser}: `);
    if (!password) {
      return { exitCode: 1, error: 'Annulé' };
    }

    // Tenter la connexion
    const result = await auth.login(targetUser, password);

    if (result.success) {
      // Mettre à jour l'environnement du shell
      this.shell.env.USER = result.user.username;
      this.shell.env.HOME = result.user.home;
      this.shell.env.UID = String(result.user.uid);
      this.shell.env.PATH = `${result.user.home}/.local/apps:/usr/bin:/bin`;
      this.shell.cwd = result.user.home;
      this.shell.env.PWD = result.user.home;
      this.shell.user = result.user;

      return { exitCode: 0, output: `Connecté en tant que ${targetUser}\n` };
    } else {
      return { exitCode: 1, error: result.error };
    }
  }

  /**
   * sudo - Exécute une commande en tant qu'admin
   */
  async sudo(args) {
    if (args.length === 0) {
      return { exitCode: 1, error: 'Usage: sudo <commande> [args...]' };
    }

    const auth = getAuth();

    // Vérifier que l'utilisateur peut utiliser sudo
    const canSudo = await auth.canSudo();
    if (!canSudo) {
      return { exitCode: 1, error: `${this.shell.env.USER} n'est pas dans le fichier sudoers. Cet incident sera signalé.` };
    }

    // Vérifier/demander le mot de passe
    const sudoSession = auth.sudoSession;
    const needPassword = !sudoSession ||
                         sudoSession.user !== this.shell.env.USER ||
                         Date.now() >= sudoSession.expiresAt;

    if (needPassword) {
      const password = await this.promptPassword(`[sudo] Mot de passe de ${this.shell.env.USER}: `);
      if (!password) {
        return { exitCode: 1, error: 'Annulé' };
      }

      const valid = await auth.verifySudo(password);
      if (!valid) {
        return { exitCode: 1, error: 'Mot de passe incorrect' };
      }
    }

    // Exécuter la commande
    const command = args[0];
    const cmdArgs = args.slice(1);

    // Exécuter la commande (built-in ou externe)
    if (this.commands.has(command)) {
      return await this.execute(command, cmdArgs);
    } else {
      // Commande externe - le shell la gérera
      return { exitCode: 0, runExternal: { command, args: cmdArgs } };
    }
  }

  /**
   * useradd - Crée un nouvel utilisateur (sudo requis)
   */
  async useradd(args) {
    const { options, positional } = this.parseArgs(args);
    const auth = getAuth();

    if (positional.length === 0) {
      return { exitCode: 1, error: 'Usage: useradd [-s] <nom_utilisateur>\n  -s  Donner les droits sudo' };
    }

    const username = positional[0];

    // Vérifier sudo
    const canSudo = await auth.canSudo();
    if (!canSudo) {
      return { exitCode: 1, error: 'Cette commande nécessite les privilèges administrateur (sudo)' };
    }

    // Vérifier mot de passe sudo
    const password = await this.promptPassword(`[sudo] Mot de passe de ${this.shell.env.USER}: `);
    const valid = await auth.verifySudo(password);
    if (!valid) {
      return { exitCode: 1, error: 'Mot de passe incorrect' };
    }

    // Demander le mot de passe du nouvel utilisateur
    const newPassword = await this.promptPassword(`Mot de passe pour ${username}: `);
    if (!newPassword || newPassword.length < 4) {
      return { exitCode: 1, error: 'Le mot de passe doit faire au moins 4 caractères' };
    }

    const confirmPassword = await this.promptPassword('Confirmer le mot de passe: ');
    if (newPassword !== confirmPassword) {
      return { exitCode: 1, error: 'Les mots de passe ne correspondent pas' };
    }

    // Créer l'utilisateur
    const result = await auth.createUser(username, newPassword, {
      sudo: options.s === true
    });

    if (result.success) {
      const sudoMsg = options.s ? ' (avec droits sudo)' : '';
      return { exitCode: 0, output: `Utilisateur ${username} créé${sudoMsg}.\n` };
    } else {
      return { exitCode: 1, error: result.error };
    }
  }

  /**
   * userdel - Supprime un utilisateur (sudo requis)
   */
  async userdel(args) {
    const { options, positional } = this.parseArgs(args);
    const auth = getAuth();

    if (positional.length === 0) {
      return { exitCode: 1, error: 'Usage: userdel [-r] <nom_utilisateur>\n  -r  Supprimer le répertoire home' };
    }

    const username = positional[0];

    // Vérifier sudo
    const canSudo = await auth.canSudo();
    if (!canSudo) {
      return { exitCode: 1, error: 'Cette commande nécessite les privilèges administrateur (sudo)' };
    }

    // Vérifier mot de passe sudo
    const password = await this.promptPassword(`[sudo] Mot de passe de ${this.shell.env.USER}: `);
    const valid = await auth.verifySudo(password);
    if (!valid) {
      return { exitCode: 1, error: 'Mot de passe incorrect' };
    }

    // Supprimer l'utilisateur
    const result = await auth.deleteUser(username, options.r === true);

    if (result.success) {
      const homeMsg = options.r ? ' (home supprimé)' : '';
      return { exitCode: 0, output: `Utilisateur ${username} supprimé${homeMsg}.\n` };
    } else {
      return { exitCode: 1, error: result.error };
    }
  }

  /**
   * users - Liste les utilisateurs
   */
  async users() {
    const auth = getAuth();
    const users = await auth.listUsers();

    if (users.length === 0) {
      return { exitCode: 0, output: 'Aucun utilisateur.\n' };
    }

    // Lire sudoers
    let sudoers = {};
    try {
      const sudoersContent = await this.fs.readFile('/etc/sudoers');
      sudoers = JSON.parse(sudoersContent);
    } catch (e) {
      // Ignorer
    }

    let output = '';
    for (const user of users) {
      const isAdmin = sudoers[user.username] === true;
      const adminTag = isAdmin ? ' \x1b[33m(admin)\x1b[0m' : '';
      const currentTag = user.username === this.shell.env.USER ? ' \x1b[32m*\x1b[0m' : '';
      output += `${user.username}${currentTag}${adminTag} - ${user.name} (uid=${user.uid})\n`;
    }

    return { exitCode: 0, output };
  }

  /**
   * Helper: Demande un mot de passe (masqué)
   */
  async promptPassword(prompt) {
    return new Promise(resolve => {
      this.shell.print(prompt);

      let password = '';
      const originalHandler = this.shell.inputDisposable;

      // Désactiver le handler normal
      this.shell.suspendInput();

      // Handler temporaire pour le mot de passe
      const passwordHandler = this.shell.terminal.onData(data => {
        const code = data.charCodeAt(0);

        // Entrée
        if (data === '\r') {
          this.shell.terminal.write('\r\n');
          passwordHandler.dispose();
          this.shell.resumeInput();
          resolve(password);
          return;
        }

        // Backspace
        if (data === '\x7f' || data === '\b') {
          if (password.length > 0) {
            password = password.slice(0, -1);
            // Pas de feedback visuel pour le mot de passe
          }
          return;
        }

        // Ctrl+C
        if (data === '\x03') {
          this.shell.terminal.write('^C\r\n');
          passwordHandler.dispose();
          this.shell.resumeInput();
          resolve('');
          return;
        }

        // Caractère imprimable
        if (code >= 32 && code < 127) {
          password += data;
          // Pas de feedback visuel
        }
      });
    });
  }

  /**
   * exit - Quitte le shell
   */
  async exit(args) {
    const code = parseInt(args[0]) || 0;
    this.shell.running = false;
    return { exitCode: code };
  }

  // ==========================================
  // SYSTÈME
  // ==========================================

  /**
   * hardreset - Réinitialise WebOs aux paramètres d'usine
   */
  async hardreset(args) {
    const { options } = this.parseArgs(args);

    // Vérifier si l'utilisateur a confirmé
    if (!options.y && !options.yes && !options.confirm) {
      return {
        exitCode: 0,
        output: `\x1b[1;31mATTENTION !\x1b[0m Cette commande va :
  - Supprimer TOUS les fichiers
  - Supprimer TOUS les utilisateurs
  - Supprimer TOUTES les applications installées
  - Réinitialiser WebOs comme au premier lancement

\x1b[1;33mCette action est IRREVERSIBLE !\x1b[0m

Pour confirmer, exécutez : \x1b[1mhardreset --yes\x1b[0m
`
      };
    }

    // Afficher le message de réinitialisation
    this.shell.print('\r\n\x1b[1;33mRéinitialisation de WebOs...\x1b[0m\r\n');
    this.shell.print('Suppression des données...\r\n');

    try {
      // Supprimer toutes les bases IndexedDB de WebOs
      const databases = await indexedDB.databases();
      for (const db of databases) {
        if (db.name && (db.name.includes('webos') || db.name.includes('WebOs') || db.name === 'webos-fs')) {
          indexedDB.deleteDatabase(db.name);
        }
      }

      // Supprimer aussi la base principale du filesystem
      indexedDB.deleteDatabase('webos-fs');

      this.shell.print('\x1b[1;32mDonnées supprimées.\x1b[0m\r\n');
      this.shell.print('Redémarrage dans 2 secondes...\r\n');

      // Attendre un peu puis recharger
      setTimeout(() => {
        window.location.reload();
      }, 2000);

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: `Erreur lors de la réinitialisation: ${err.message}` };
    }
  }

  // ==========================================
  // GESTIONNAIRE DE PAQUETS
  // ==========================================

  /**
   * web - Gestionnaire de paquets WebOs
   */
  async web(args) {
    if (args.length === 0) {
      return this.webHelp();
    }

    const subcommand = args[0];
    const subArgs = args.slice(1);

    switch (subcommand) {
      case 'install':
        return this.webInstall(subArgs);
      case 'uninstall':
      case 'remove':
        return this.webUninstall(subArgs);
      case 'list':
        return this.webList(subArgs);
      case 'search':
        return this.webSearch(subArgs);
      case 'info':
        return this.webInfo(subArgs);
      case 'update':
        return this.webUpdate();
      default:
        return { exitCode: 1, error: `web: sous-commande inconnue '${subcommand}'` };
    }
  }

  /**
   * web help - Affiche l'aide
   */
  async webHelp() {
    return {
      exitCode: 0,
      output: `
\x1b[1;32mweb\x1b[0m - Gestionnaire de paquets WebOs

\x1b[1;34mUsage:\x1b[0m
  web install <package>    Installe un paquet
  web install --all        Installe TOUS les paquets disponibles
  web uninstall <package>  Désinstalle un paquet
  web list                 Liste les paquets installés
  web search <query>       Recherche des paquets
  web info <package>       Affiche les informations d'un paquet
  web update               Met à jour le registre

\x1b[1;34mExemples:\x1b[0m
  web install clock        Installe l'horloge
  web install --all        Installe tous les paquets
  web search calculat      Recherche "calculat"
  web list                 Affiche les paquets installés
`
    };
  }

  /**
   * Récupère le registre des paquets depuis le serveur
   * Essaie d'abord registry.php (auto-discovery), puis registry.json (statique)
   * Utilise des chemins relatifs pour supporter les déploiements en sous-dossier
   */
  async fetchRegistry() {
    // Utiliser des chemins relatifs (le navigateur les résout par rapport à la page)
    const basePaths = ['./packages/', '../packages/', 'packages/'];

    for (const base of basePaths) {
      // Essayer d'abord le PHP (auto-discovery dynamique)
      try {
        const response = await fetch(base + 'registry.php');
        if (response.ok) {
          const data = await response.json();
          if (data && data.packages) {
            return data;
          }
        }
      } catch (err) {
        // PHP non disponible, continuer
      }

      // Essayer le JSON statique
      try {
        const response = await fetch(base + 'registry.json');
        if (response.ok) {
          const data = await response.json();
          if (data && data.packages) {
            return data;
          }
        }
      } catch (err) {
        // Continuer avec le prochain chemin
      }
    }

    throw new Error('Impossible de récupérer le registre des paquets');
  }

  /**
   * Lit les paquets installés
   */
  async getInstalledPackages() {
    const installedPath = '/var/lib/webpkg/installed.json';
    try {
      const content = await this.fs.readFile(installedPath);
      return JSON.parse(content);
    } catch (err) {
      return {};
    }
  }

  /**
   * Sauvegarde les paquets installés
   */
  async saveInstalledPackages(packages) {
    const installedPath = '/var/lib/webpkg/installed.json';
    try {
      await this.fs.mkdir('/var/lib/webpkg', { recursive: true });
      await this.fs.writeFile(installedPath, JSON.stringify(packages, null, 2));
    } catch (err) {
      log.error('Failed to save installed packages:', err);
    }
  }

  /**
   * Lit les aliases de commandes
   */
  async getAliases() {
    const aliasesPath = '/var/lib/webpkg/aliases.json';
    try {
      const content = await this.fs.readFile(aliasesPath);
      return JSON.parse(content);
    } catch (err) {
      return {};
    }
  }

  /**
   * Sauvegarde les aliases pour un package
   */
  async saveAliases(packageName, commands) {
    const aliases = await this.getAliases();

    for (const cmd of commands) {
      aliases[cmd] = packageName;
    }

    const aliasesPath = '/var/lib/webpkg/aliases.json';
    try {
      await this.fs.mkdir('/var/lib/webpkg', { recursive: true });
      await this.fs.writeFile(aliasesPath, JSON.stringify(aliases, null, 2));
    } catch (err) {
      log.error('Failed to save aliases:', err);
    }
  }

  /**
   * Supprime les aliases d'un package
   */
  async removeAliases(packageName) {
    const aliases = await this.getAliases();

    // Supprimer tous les aliases pointant vers ce package
    for (const [cmd, pkg] of Object.entries(aliases)) {
      if (pkg === packageName) {
        delete aliases[cmd];
      }
    }

    const aliasesPath = '/var/lib/webpkg/aliases.json';
    try {
      await this.fs.writeFile(aliasesPath, JSON.stringify(aliases, null, 2));
    } catch (err) {
      log.error('Failed to remove aliases:', err);
    }
  }

  /**
   * web install - Installe un paquet
   */
  async webInstall(args, isDepInstall = false) {
    const { options, positional } = this.parseArgs(args);

    // Gestion du flag --all pour installer tous les paquets
    if (options.all) {
      return this.webInstallAll();
    }

    if (positional.length === 0) {
      return { exitCode: 1, error: 'Usage: web install <package> ou web install --all' };
    }

    const packageName = positional[0];

    try {
      // Vérifier si déjà installé
      const installed = await this.getInstalledPackages();
      if (installed[packageName]) {
        if (isDepInstall) {
          // Dépendance déjà installée, c'est ok
          return { exitCode: 0 };
        }
        return { exitCode: 1, error: `${packageName}: déjà installé` };
      }

      // Récupérer le registre
      if (!isDepInstall) {
        this.shell.print(`\x1b[36m:: \x1b[0mRécupération du registre...\n`);
      }
      const registry = await this.fetchRegistry();

      // Vérifier que le paquet existe
      const pkg = registry.packages[packageName];
      if (!pkg) {
        return { exitCode: 1, error: `${packageName}: paquet introuvable` };
      }

      // Installer les dépendances d'abord
      if (pkg.dependencies && typeof pkg.dependencies === 'object') {
        const deps = Object.keys(pkg.dependencies);
        if (deps.length > 0) {
          this.shell.print(`\x1b[36m:: \x1b[0mRésolution des dépendances de ${packageName}...\n`);
          for (const dep of deps) {
            const depInstalled = await this.getInstalledPackages();
            if (!depInstalled[dep]) {
              this.shell.print(`   \x1b[33m→\x1b[0m Installation de la dépendance: ${dep}\n`);
              const depResult = await this.webInstall([dep], true);
              if (depResult.exitCode !== 0) {
                return { exitCode: 1, error: `Échec de l'installation de la dépendance ${dep}: ${depResult.error}` };
              }
            }
          }
        }
      }

      this.shell.print(`\x1b[36m:: \x1b[0mInstallation de ${packageName} v${pkg.version}...\n`);

      // Créer le répertoire de l'app
      const appPath = `/usr/bin/${packageName}`;
      await this.fs.mkdir(appPath, { recursive: true });

      // Télécharger et installer chaque fichier
      for (const file of pkg.files) {
        this.shell.print(`   \x1b[32m→\x1b[0m ${file}\n`);

        const response = await fetch(`./packages/${packageName}/${file}`);
        if (!response.ok) {
          throw new Error(`Impossible de télécharger ${file}`);
        }

        const content = await response.text();
        await this.fs.writeFile(`${appPath}/${file}`, content);
      }

      // Lire le manifest installé pour les infos complètes
      const manifestContent = await this.fs.readFile(`${appPath}/manifest.json`);
      const manifest = JSON.parse(manifestContent);

      // Enregistrer dans les paquets installés
      const updatedInstalled = await this.getInstalledPackages();
      updatedInstalled[packageName] = {
        version: pkg.version,
        installedAt: Date.now(),
        type: pkg.type,
        autostart: pkg.autostart || manifest.autostart || false,
        dependencies: pkg.dependencies || {},
        provides: manifest.provides || []
      };
      await this.saveInstalledPackages(updatedInstalled);

      // Créer les aliases pour les commandes fournies
      if (manifest.provides && Array.isArray(manifest.provides)) {
        await this.saveAliases(packageName, manifest.provides);
        this.shell.print(`   \x1b[32m→\x1b[0m Commandes ajoutées: ${manifest.provides.join(', ')}\n`);
      }

      // Gérer les manuels et l'enregistrement dans help
      const providedCommands = manifest.provides || [];

      // Vérifier que chaque commande fournie a son manuel
      if (providedCommands.length > 0) {
        const missingManuals = [];

        for (const cmd of providedCommands) {
          if (!manifest.manPages || !manifest.manPages[cmd]) {
            missingManuals.push(cmd);
          }
        }

        if (missingManuals.length > 0) {
          this.shell.print(`   \x1b[31m✗\x1b[0m Manuels manquants pour: ${missingManuals.join(', ')}\n`);
          this.shell.print(`   \x1b[33m⚠\x1b[0m Le package doit inclure un manuel pour chaque commande dans 'provides'\n`);
          // On continue quand même l'installation mais on avertit
        }

        // Installer les manuels individuels et enregistrer dans help
        if (manifest.manPages) {
          for (const cmd of providedCommands) {
            if (manifest.manPages[cmd]) {
              await this.manPages.installManual(cmd, manifest.manPages[cmd]);
            }
          }
          this.shell.print(`   \x1b[32m→\x1b[0m Manuels installés: ${Object.keys(manifest.manPages).join(', ')}\n`);
        }

        // Enregistrer chaque commande dans help
        if (manifest.help && manifest.help.commands) {
          const category = manifest.help.category || 'Applications';
          for (const [cmd, description] of Object.entries(manifest.help.commands)) {
            await this.manPages.registerCommand(cmd, description, category);
          }
        }
      } else {
        // Package simple (une seule commande = le nom du package)
        if (manifest.help) {
          await this.manPages.registerCommand(
            manifest.help.usage || packageName,
            manifest.help.shortDescription || manifest.description,
            manifest.help.category || 'Applications'
          );
        }

        // Installer le manuel principal
        if (manifest.man) {
          await this.manPages.installManual(packageName, manifest.man);
          this.shell.print(`   \x1b[32m→\x1b[0m Manuel installé\n`);
        } else {
          this.shell.print(`   \x1b[33m⚠\x1b[0m Pas de manuel fourni (champ 'man' manquant)\n`);
        }
      }

      this.shell.print(`\x1b[32m✓\x1b[0m ${packageName} installé avec succès\n`);

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * web install --all - Installe tous les paquets disponibles
   */
  async webInstallAll() {
    try {
      this.shell.print(`\x1b[1;36m:: \x1b[0mRécupération du registre...\n`);
      const registry = await this.fetchRegistry();
      const packages = Object.keys(registry.packages);

      if (packages.length === 0) {
        return { exitCode: 0, output: 'Aucun paquet disponible dans le registre.' };
      }

      const installed = await this.getInstalledPackages();
      const toInstall = packages.filter(pkg => !installed[pkg]);

      if (toInstall.length === 0) {
        return { exitCode: 0, output: '\x1b[32mTous les paquets sont déjà installés !\x1b[0m' };
      }

      this.shell.print(`\x1b[1;36m:: \x1b[0m${toInstall.length} paquets à installer:\n`);
      this.shell.print(`   ${toInstall.join(', ')}\n\n`);

      let successCount = 0;
      let failCount = 0;

      for (const packageName of toInstall) {
        this.shell.print(`\x1b[36m[${successCount + failCount + 1}/${toInstall.length}]\x1b[0m Installation de ${packageName}...\n`);
        const result = await this.webInstall([packageName], true);
        if (result.exitCode === 0) {
          successCount++;
        } else {
          failCount++;
          this.shell.print(`   \x1b[31m✗\x1b[0m Échec: ${result.error || 'erreur inconnue'}\n`);
        }
      }

      this.shell.print(`\n\x1b[1;32m:: \x1b[0mInstallation terminée !\n`);
      this.shell.print(`   \x1b[32m✓\x1b[0m ${successCount} paquet(s) installé(s)\n`);
      if (failCount > 0) {
        this.shell.print(`   \x1b[31m✗\x1b[0m ${failCount} échec(s)\n`);
      }

      return { exitCode: failCount > 0 ? 1 : 0 };
    } catch (err) {
      return { exitCode: 1, error: `Erreur: ${err.message}` };
    }
  }

  /**
   * web uninstall - Désinstalle un paquet
   */
  async webUninstall(args) {
    if (args.length === 0) {
      return { exitCode: 1, error: 'Usage: web uninstall <package>' };
    }

    const packageName = args[0];

    try {
      // Vérifier si installé
      const installed = await this.getInstalledPackages();
      if (!installed[packageName]) {
        return { exitCode: 1, error: `${packageName}: non installé` };
      }

      this.shell.print(`\x1b[36m:: \x1b[0mDésinstallation de ${packageName}...\n`);

      // Supprimer les aliases fournis par ce package
      if (installed[packageName].provides && installed[packageName].provides.length > 0) {
        await this.removeAliases(packageName);
        this.shell.print(`   \x1b[33m→\x1b[0m Commandes retirées: ${installed[packageName].provides.join(', ')}\n`);
      }

      // Supprimer le répertoire de l'app
      const appPath = `/usr/bin/${packageName}`;
      try {
        await this.fs.delete(appPath, { recursive: true });
      } catch (e) {
        // Ignorer si le dossier n'existe pas
      }

      // Récupérer les commandes fournies avant suppression
      const provides = installed[packageName]?.provides || [];

      // Retirer des paquets installés
      delete installed[packageName];
      await this.saveInstalledPackages(installed);

      // Retirer du help et supprimer les manuels
      if (provides.length > 0) {
        // Package multi-commandes : supprimer chaque commande
        for (const cmd of provides) {
          try {
            await this.manPages.unregisterCommand(cmd);
          } catch (e) {
            // Ignorer
          }
        }
      } else {
        // Package simple
        try {
          await this.manPages.unregisterCommand(packageName);
        } catch (e) {
          // Ignorer
        }
      }

      this.shell.print(`\x1b[32m✓\x1b[0m ${packageName} désinstallé\n`);

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * web list - Liste les paquets installés
   */
  async webList(args) {
    const { options } = this.parseArgs(args);

    try {
      const installed = await this.getInstalledPackages();
      const packages = Object.keys(installed);

      if (packages.length === 0) {
        return { exitCode: 0, output: 'Aucun paquet installé.\n' };
      }

      let output = '\x1b[1;32mPaquets installés:\x1b[0m\n\n';

      for (const name of packages.sort()) {
        const pkg = installed[name];
        const type = pkg.type === 'daemon' ? '\x1b[33m[daemon]\x1b[0m ' :
                     pkg.type === 'gui' ? '\x1b[35m[gui]\x1b[0m ' :
                     pkg.type === 'tui' ? '\x1b[36m[tui]\x1b[0m ' : '';
        const auto = pkg.autostart ? '\x1b[32m(autostart)\x1b[0m' : '';

        output += `  ${type}${name} \x1b[2mv${pkg.version}\x1b[0m ${auto}\n`;
      }

      output += `\nTotal: ${packages.length} paquet(s)\n`;

      return { exitCode: 0, output };
    } catch (err) {
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * web search - Recherche des paquets
   */
  async webSearch(args) {
    const query = args.join(' ').toLowerCase();

    try {
      this.shell.print(`\x1b[36m:: \x1b[0mRecherche dans le registre...\n\n`);

      const registry = await this.fetchRegistry();
      const installed = await this.getInstalledPackages();

      const results = [];

      for (const [name, pkg] of Object.entries(registry.packages)) {
        const searchText = `${name} ${pkg.description} ${pkg.category || ''}`.toLowerCase();

        if (!query || searchText.includes(query)) {
          results.push({ name, ...pkg });
        }
      }

      if (results.length === 0) {
        return { exitCode: 0, output: 'Aucun paquet trouvé.\n' };
      }

      let output = '';

      for (const pkg of results) {
        const isInstalled = installed[pkg.name];
        const installedMark = isInstalled ? '\x1b[32m[installé]\x1b[0m ' : '';
        const type = pkg.type === 'daemon' ? '\x1b[33m[daemon]\x1b[0m ' :
                     pkg.type === 'gui' ? '\x1b[35m[gui]\x1b[0m ' :
                     pkg.type === 'tui' ? '\x1b[36m[tui]\x1b[0m ' : '';

        output += `\x1b[1m${pkg.name}\x1b[0m v${pkg.version} ${type}${installedMark}\n`;
        output += `    ${pkg.description}\n\n`;
      }

      return { exitCode: 0, output };
    } catch (err) {
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * web info - Affiche les infos d'un paquet
   */
  async webInfo(args) {
    if (args.length === 0) {
      return { exitCode: 1, error: 'Usage: web info <package>' };
    }

    const packageName = args[0];

    try {
      const registry = await this.fetchRegistry();
      const pkg = registry.packages[packageName];

      if (!pkg) {
        return { exitCode: 1, error: `${packageName}: paquet introuvable` };
      }

      const installed = await this.getInstalledPackages();
      const isInstalled = installed[packageName];

      let output = `
\x1b[1;32m${pkg.name}\x1b[0m v${pkg.version}
\x1b[2m${'─'.repeat(40)}\x1b[0m

\x1b[1mDescription:\x1b[0m  ${pkg.description}
\x1b[1mType:\x1b[0m         ${pkg.type}
\x1b[1mAuteur:\x1b[0m       ${pkg.author || 'Inconnu'}
\x1b[1mCatégorie:\x1b[0m    ${pkg.category || 'Autre'}
\x1b[1mTaille:\x1b[0m       ${this.formatSize(pkg.size || 0)}
\x1b[1mFichiers:\x1b[0m     ${pkg.files?.join(', ') || 'N/A'}
`;

      if (isInstalled) {
        const date = new Date(isInstalled.installedAt).toLocaleDateString('fr-FR');
        output += `\n\x1b[32mInstallé le:\x1b[0m  ${date}`;
        if (isInstalled.autostart) {
          output += `\n\x1b[33mAutostart:\x1b[0m    Oui`;
        }
      } else {
        output += `\n\x1b[2mNon installé\x1b[0m`;
      }

      output += '\n';

      return { exitCode: 0, output };
    } catch (err) {
      return { exitCode: 1, error: err.message };
    }
  }

  /**
   * web update - Met à jour le registre (placeholder)
   */
  async webUpdate() {
    try {
      this.shell.print(`\x1b[36m:: \x1b[0mMise à jour du registre...\n`);

      // Simplement récupérer le registre pour valider la connexion
      await this.fetchRegistry();

      this.shell.print(`\x1b[32m✓\x1b[0m Registre à jour\n`);

      return { exitCode: 0 };
    } catch (err) {
      return { exitCode: 1, error: err.message };
    }
  }
}

export default Commands;
